import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { nullableStringSetter } from '@/helpers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, Field, Form, Icon, TextArea, Typography } from '@/components';

// ─── Types ───────────────────────────────────────────────────────────────────

export type DeclineFormFields = {
  feedback_reject: string;
  internal_notes_reject: string | null;
};

type SellerDeclineEoiFormProps = {
  readonly companyName: string;
  readonly onBack: () => void;
  readonly onSubmit: (data: DeclineFormFields) => void;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const TEXTAREA_MIN_LENGTH = 100;
const TEXTAREA_MAX_LENGTH = 2000;

// ─── Component ───────────────────────────────────────────────────────────────

export function SellerRejectEoiForm({ companyName, onBack, onSubmit }: SellerDeclineEoiFormProps) {
  const { t } = useTranslation(['offers']);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (data: DeclineFormFields) => {
    setIsLoading(true);
    try {
      onSubmit(data);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="mb-6 border border-red-200 bg-red-50">
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center text-red-900">
          <Icon name="x" className="mr-2 h-4 w-4 text-red-600" />
          {t('Decline EoI')}
        </CardTitle>
        <button
          onClick={onBack}
          className="flex cursor-pointer items-center gap-1 text-slate-600 transition-colors hover:text-slate-900"
        >
          <Icon name="chevron-left" className="h-4 w-4" />
          <span className="text-sm font-medium">{t('Back')}</span>
        </button>
      </CardHeader>

      <CardContent>
        <div className="mb-6">
          <Typography size="body-3" color="secondary">
            {t(
              'Please, share your feedback to the prospective buyer and any private notes you want to share with nTeaser.'
            )}
          </Typography>
        </div>

        <Form<DeclineFormFields> onSubmit={handleSubmit}>
          {({ formState, register }) => (
            <div className="space-y-6">
              {/* Feedback for buyer */}
              <Field
                id="feedback_reject"
                label={t('FEEDBACK FOR {{company}}', { company: companyName.toUpperCase() })}
                error={formState.errors['feedback_reject']}
                delay={0}
                classes={{ field: 'space-y-1', label: 'text-xs font-bold text-red-900' }}
              >
                <Typography size="body-3" color="secondary">
                  {t('Visible to the seller.')}
                </Typography>
                <TextArea
                  className="mt-4 bg-white"
                  id="feedback_reject"
                  color="primary"
                  placeholder={t('Type here...')}
                  minLength={TEXTAREA_MIN_LENGTH}
                  maxLength={TEXTAREA_MAX_LENGTH}
                  registration={register('feedback_reject', {
                    required: t('This field is required'),
                    setValueAs: nullableStringSetter,
                    maxLength: TEXTAREA_MAX_LENGTH,
                    minLength: {
                      value: TEXTAREA_MIN_LENGTH,
                      message: t('Minimum {{min}} characters', { min: TEXTAREA_MIN_LENGTH }),
                    },
                  })}
                />
              </Field>

              <hr />

              {/* Private notes */}
              <Field
                id="internal_notes_reject"
                label={t('PRIVATE NOTES')}
                error={formState.errors['internal_notes_reject']}
                delay={0}
                classes={{ field: 'space-y-1', label: 'text-xs font-bold text-red-900' }}
              >
                <Typography size="body-3" color="secondary">
                  {t('Only visible to the nTeaser team.')}
                </Typography>
                <TextArea
                  className="mt-4 bg-white"
                  id="internal_notes_reject"
                  color="primary"
                  placeholder={t('Anything we should know? (no minimum)')}
                  registration={register('internal_notes_reject', {
                    required: false,
                  })}
                />
              </Field>

              <Button type="submit" color="secondary" disabled={isLoading} className="w-full">
                <Icon name="arrow-right" className="h-4 w-4" />
                {isLoading ? t('Sending...') : t('Submit decline')}
              </Button>
            </div>
          )}
        </Form>
      </CardContent>
    </Card>
  );
}
